/*
 * The AI read of a received fax, as Communication > Fax shows it (10-05).
 *
 * The server reads the first 5 pages of each new fax with the cheapest model
 * we run and keeps the result next to fax_master (v2 branch v2-fax-ai). Each
 * GET /v1/{pid}/fax/get-list row then carries it as `ai`:
 *
 *   ai: {
 *     status: "pending" | "done" | "error" | "skipped",
 *     doc_type: "referral",              // fixed list
 *     label: "Sleep study referral",     // <= 5 words; the pill
 *     summary: "...",                    // one line, no identifiers
 *     pages_total, pages_sent,
 *     match_status: "matched" | "multiple" | "possible" | "none",
 *     suggested_patient: { patientid, firstname, lastname, dob } | null,
 *     candidates: [{ patientid, firstname, lastname, dob }],   // "multiple"
 *   }
 *
 * A row without `ai` (an older v2, or a fax that was never read) shows
 * nothing. "error" and "skipped" show nothing either: staff read the PDF as
 * before. The patient is only ever a suggestion ("Possibly: ..."), a link to
 * that patient on the dashboard; nothing here files the fax to a chart. Send
 * to Athena (10-06) opens with that chart and a class from the label
 * pre-selected (faxAiAthenaClass, faxAiUniquePatient below); staff confirm
 * or change them, and only their Upload sends anything.
 *
 * Also read, for the stored column names: `fax_ai` for `ai`, and
 * match_patientid + pt_first/pt_last/pt_dob, match_candidates (array or JSON
 * text) in place of suggested_patient / candidates.
 *
 * What v2-fax-ai actually sends (api/ringcentral/practice/ai_view.py), and
 * how it is read here:
 *
 *   ai: { status: "pending" | "done" | "error",
 *         label: "sleep_study_report",          // slug -> prettified
 *         label_display: "Sleep study report",  // the pill when present
 *         summary,                              // 10-06 (prompt fax-v2): 2-3 sentences
 *         action: "Sign and return CPAP order by 10/10" | null,   // fax-v2: what the
 *                                               // clinic must do; null = nothing asked
 *         pages_total, pages_sent,
 *         match_status: "unique" | "multiple" | "none" | "no_identity" | "unavailable",
 *         suggested_patientid, match_confidence }
 *
 * The list carries no patient name or DOB. For "unique" / "multiple" the
 * viewer asks GET /v1/{pid}/fax/{fax_id}/ai for the chart(s) when that fax
 * is opened (faxAiParts.jsx, faxApi.js faxAiDetail), and shows how close the
 * match is (faxAiMatchHint).
 */

export const FAX_AI_PENDING_TEXT = "Summarizing…";

// Re-read the list while a listed fax is still being read: once a minute, at
// most this many times for the same set of pending faxes (faxtable.jsx).
export const FAX_AI_POLL_MS = 60 * 1000;
export const FAX_AI_POLL_MAX = 5;

const PENDING = new Set(["pending", "queued", "processing", "running"]);
const DONE = new Set(["done", "completed", "complete", "ok", "success"]);
const NO_PATIENT = new Set(["possible", "none", "no_match", "nomatch", "no_identity", "unavailable"]);
const ONE_PATIENT = new Set(["matched", "unique"]);
const MAX_CANDIDATES = 3;
const SLUG = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

const text = (v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");

// The server stores "nothing asked" as null; also never show its spellings.
const NO_ACTION = /^(?:none|n\/?a|na|no action(?: needed| required)?|nothing(?: requested)?|-+)\.?$/i;
const actionOf = (ai) => {
  const a = text(ai.action);
  return a && !NO_ACTION.test(a) ? a : "";
};
const present = (v) => v !== undefined && v !== null && String(v).trim() !== "";

// "sleep_study_report" -> "Sleep study report"
const docTypeLabel = (t) => {
  const s = text(t).replace(/_/g, " ");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

// The pill: v2's label_display, else a label (prettified when it is a slug,
// as v2's `label` is), else the doc_type.
const labelOf = (ai) => {
  const shown = text(ai.label_display);
  if (shown) return shown;
  const label = text(ai.label);
  if (label) return SLUG.test(label) ? docTypeLabel(label) : label;
  return docTypeLabel(ai.doc_type);
};

const confidenceOf = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/*
 * How close the suggested chart is, in words (the server's confidence tiers,
 * api/ringcentral/practice/ai_match.py). Shown in grey after the link so
 * staff can weigh it before opening the chart. "" when unknown.
 */
export function faxAiMatchHint(confidence) {
  const c = confidenceOf(confidence);
  if (c === null) return "";
  if (c >= 1) return "name and DOB match";
  if (c >= 0.85) return "similar name, same DOB";
  if (c >= 0.75) return "first/last name swapped, same DOB";
  return "DOB day/month swapped";
}

const aiOf = (row) => {
  if (!row || typeof row !== "object") return null;
  const box = row.ai !== undefined && row.ai !== null ? row.ai : row.fax_ai;
  return box && typeof box === "object" && !Array.isArray(box) ? box : null;
};

// patient_master.dob holds both YYYY-MM-DD and MM/DD/YYYY: show MM/DD/YYYY.
export function formatFaxAiDob(dob) {
  const s = text(dob === undefined || dob === null ? "" : String(dob));
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T ])/.exec(s);
  if (m) return `${m[2].padStart(2, "0")}/${m[3].padStart(2, "0")}/${m[1]}`;
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
  return s;
}

const patientOf = (p) => {
  if (!p || typeof p !== "object" || !present(p.patientid)) return null;
  const name =
    text(p.name) ||
    text(p.patient_name) ||
    [text(p.firstname || p.first_name || p.first), text(p.lastname || p.last_name || p.last)]
      .filter(Boolean)
      .join(" ");
  if (!name) return null;
  return { patientid: p.patientid, name, dob: formatFaxAiDob(p.dob) };
};

const listOf = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// The chart(s) the server suggests: one when it matched one patient, up to
// three when several charts match (none is picked), else none.
const suggestionsOf = (ai) => {
  const status = text(ai.match_status).toLowerCase();
  if (NO_PATIENT.has(status)) return [];
  const one =
    patientOf(ai.suggested_patient) ||
    patientOf(ai.match) ||
    (present(ai.match_patientid)
      ? patientOf({ patientid: ai.match_patientid, name: ai.match_name, first: ai.pt_first, last: ai.pt_last, dob: ai.match_dob || ai.pt_dob })
      : null);
  if (one && status !== "multiple") return [one];
  const many = candidatesOf(ai.candidates !== undefined ? ai.candidates : ai.match_candidates);
  if (many.length) return many;
  return one ? [one] : [];
};

function candidatesOf(list) {
  const seen = new Set();
  const many = [];
  for (const c of listOf(list)) {
    const p = patientOf(c);
    if (!p || seen.has(String(p.patientid))) continue;
    seen.add(String(p.patientid));
    many.push(p);
  }
  return many.slice(0, MAX_CANDIDATES);
}

// v2 sends only ids in the list: the viewer fetches the chart(s) for these.
const detailWanted = (ai, status) =>
  status === "multiple" || (ONE_PATIENT.has(status) && present(ai.suggested_patientid));

/*
 * What to show for a list row:
 *   null                         nothing (no AI read, or it failed)
 *   { pending: true }            "Summarizing..."
 *   { pending: false, label, summary, action, suggestions: [{patientid, name, dob}],
 *     confidence, detail }       detail: the viewer must fetch the chart(s)
 *                                (v2 lists ids only), see faxAiDetailView;
 *                                action: "" when nothing is asked (or an older read)
 */
export function faxAiView(row) {
  const ai = aiOf(row);
  if (!ai) return null;
  const status = text(ai.status).toLowerCase();
  if (PENDING.has(status))
    return { pending: true, label: "", summary: "", action: "", suggestions: [], confidence: null, detail: false };
  if (status && !DONE.has(status)) return null;
  const label = labelOf(ai);
  const summary = text(ai.summary);
  const action = actionOf(ai);
  const suggestions = suggestionsOf(ai);
  const matchStatus = text(ai.match_status).toLowerCase();
  const detail = !suggestions.length && detailWanted(ai, matchStatus);
  if (!label && !summary && !action && !suggestions.length && !detail) return null;
  const confidence =
    matchStatus === "multiple" ? null : confidenceOf(ai.match_confidence !== undefined ? ai.match_confidence : ai.confidence);
  return { pending: false, label, summary, action, suggestions, confidence, detail };
}

/*
 * The chart(s) from GET /v1/{pid}/fax/{fax_id}/ai's `data` (v2 ai_view.detail):
 *   { suggestions: [{patientid, name, dob}], confidence } or null.
 * "unique": the one suggested chart (with its confidence); "multiple": up to
 * three candidates, none picked; anything else (or a chart the server could
 * not resolve): null, so nothing is shown.
 */
export function faxAiDetailView(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  if (!DONE.has(text(data.status).toLowerCase())) return null;
  const match = data.match;
  if (!match || typeof match !== "object") return null;
  const status = text(match.status).toLowerCase();
  if (ONE_PATIENT.has(status)) {
    const one = patientOf(match.suggested);
    return one ? { suggestions: [one], confidence: confidenceOf(match.confidence) } : null;
  }
  if (status === "multiple") {
    const many = candidatesOf(match.candidates);
    return many.length ? { suggestions: many, confidence: null } : null;
  }
  return null;
}

/*
 * Send to Athena pre-fill (10-06, sendtoathena.jsx). Only a pre-selection:
 * staff can change both, and nothing is sent until they press Upload.
 *
 * Class: the AI label picks the category the modal opens on, using only the
 * two upload routes that exist today:
 *   IMAGING   v1 fax-documents/add-imaging-result (filed closed, autoclose)
 *   CLINICAL  v1 fax-documents/add-clinical-document + documentsubclass
 * Everything but an imaging report is CLINICAL / CLINICALDOCUMENT, which
 * Athena routes for review (a DME order needs a provider to see it).
 * `other` and `records_request` get the same class without the "from the AI
 * label" hint: staff pick e.g. CONSULTNOTE or ADMISSIONDISCHARGE by hand.
 */
export const FAX_AI_ATHENA_CLASS = Object.freeze({
  imaging_report: { category: "IMAGING", subclass: null, hint: true },
  sleep_study_report: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  lab_result: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  referral: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  dme_cpap_order: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  prior_auth_insurance: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  prescription_refill: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: true },
  records_request: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: false },
  other: { category: "CLINICAL", subclass: "CLINICALDOCUMENT", hint: false },
});

// The label slug of a read fax (v2's `label`, else the older `doc_type`).
const labelSlugOf = (ai) => {
  for (const v of [ai.label, ai.doc_type]) {
    const s = text(v).toLowerCase();
    if (Object.prototype.hasOwnProperty.call(FAX_AI_ATHENA_CLASS, s)) return s;
  }
  return "";
};

/*
 * The class to open Send to Athena on for this fax:
 *   { category: "IMAGING" | "CLINICAL", subclass: "CLINICALDOCUMENT" | null,
 *     label: the pill text to cite ("" = no hint) }
 * or null (no finished read, or a label outside the list): the modal keeps
 * its own default.
 */
export function faxAiAthenaClass(row) {
  const view = faxAiView(row);
  if (!view || view.pending) return null;
  const slug = labelSlugOf(aiOf(row));
  if (!slug) return null;
  const c = FAX_AI_ATHENA_CLASS[slug];
  return { category: c.category, subclass: c.subclass, label: c.hint ? view.label || docTypeLabel(slug) : "" };
}

// The server matched the fax to exactly one chart ("unique" / "matched").
export function faxAiMatchIsUnique(row) {
  const view = faxAiView(row);
  if (!view || view.pending) return false;
  return ONE_PATIENT.has(text(aiOf(row).match_status).toLowerCase());
}

/*
 * The one chart to pre-select in Send to Athena, or null:
 *   { patientid, name, dob, confidence }
 * Only for a unique match, and only with a name to show (from the row, or
 * `detail` = faxAiDetailView of GET fax/{id}/ai for v2's id-only rows) whose
 * id is the row's suggested_patientid. Several charts, none, no identity or
 * an unresolved chart: null, so staff search as before. patient_master's id
 * is the Athena patient id for 9928 and 7825, so it is sent as is.
 */
export function faxAiUniquePatient(row, detail) {
  if (!faxAiMatchIsUnique(row)) return null;
  const view = faxAiView(row);
  const ai = aiOf(row);
  let one = view.suggestions.length === 1 ? view.suggestions[0] : null;
  let confidence = view.confidence;
  if (!one && detail && Array.isArray(detail.suggestions) && detail.suggestions.length === 1) {
    one = detail.suggestions[0];
    if (detail.confidence !== null && detail.confidence !== undefined) confidence = detail.confidence;
  }
  if (!one || !/^\d+$/.test(String(one.patientid)) || Number(one.patientid) <= 0) return null;
  if (present(ai.suggested_patientid) && String(ai.suggested_patientid) !== String(one.patientid)) return null;
  return { patientid: one.patientid, name: one.name, dob: one.dob, confidence };
}

// The row ids of listed faxes still being read, as one key ("" when none).
export function faxAiPendingKey(rows) {
  return (rows || [])
    .filter((r) => faxAiView(r)?.pending)
    .map((r) => String(r.faxid !== undefined ? r.faxid : r.id))
    .sort()
    .join(",");
}
